import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, ReactNode } from "react";
import {
  WIDGET_REGISTRY,
  filterWidgetIdsByTier,
} from "@/data/dashboardWidgets";
import { TEMPLATE_MAP, DashboardTemplate } from "@/data/dashboardTemplates";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import React from "react";

const STORAGE_KEY = "dashboard-pinned-widgets";
const TEMPLATE_KEY = "dashboard-active-template";
const CME_KEY = "dashboard-cme-collapsed";
const FILTERS_KEY = "dashboard-widget-filters";

type WidgetFilters = Record<string, Record<string, unknown>>;

// New users start with an empty dashboard and a professional onboarding
// prompt to choose their widgets. Returning users load from localStorage.
function getDefaults(): string[] {
  return [];
}

function loadLocal(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function loadFiltersLocal(): WidgetFilters {
  try {
    const stored = loadLocal(FILTERS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
    }
  } catch {}
  return {};
}

// Strip any saved widget IDs that no longer exist in the registry. This
// lets us cleanly retire widgets (e.g., "market-snapshot" in 2026-04) without
// leaving existing users staring at a broken error card on the dashboard.
function stripDeadWidgets(saved: string[]): string[] {
  const validIds = new Set(WIDGET_REGISTRY.map(w => w.id));
  return saved.filter(id => validIds.has(id));
}

function mergeNewDefaults(saved: string[]): string[] {
  const cleaned = stripDeadWidgets(saved);
  const newDefaults = WIDGET_REGISTRY.filter(w => w.defaultPinned && !cleaned.includes(w.id)).map(w => w.id);
  return newDefaults.length > 0 ? [...cleaned, ...newDefaults] : cleaned;
}

function loadPinnedLocal(): string[] {
  try {
    const stored = loadLocal(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return mergeNewDefaults(parsed);
    }
  } catch {}
  return getDefaults();
}

function saveLocal(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch {}
}

interface DashboardWidgetsContextValue {
  pinnedWidgets: string[];
  addWidget: (id: string) => void;
  removeWidget: (id: string) => void;
  isPinned: (id: string) => boolean;
  toggleWidget: (id: string) => void;
  reorderWidgets: (fromIndex: number, toIndex: number) => void;
  activeTemplate: string | null;
  applyTemplate: (id: string) => DashboardTemplate | null;
  applyUserTemplate: (pinned: string[], filters: WidgetFilters, templateId: string) => void;
  clearTemplate: () => void;
  cmeCollapsed: boolean;
  setCmeCollapsed: (collapsed: boolean) => void;
  widgetFilters: WidgetFilters;
  setWidgetFilter: (widgetId: string, filters: Record<string, unknown> | ((prev: Record<string, unknown> | undefined) => Record<string, unknown>)) => void;
  getWidgetFilters: (widgetId: string) => Record<string, unknown> | undefined;
}

const DashboardWidgetsContext = createContext<DashboardWidgetsContextValue | null>(null);

/** Upsert preferences to DB (fire-and-forget) */
function persistToDb(
  userId: string | null,
  pinned: string[],
  template: string | null,
  cmeCollapsed: boolean,
  widgetFilters: WidgetFilters = {},
) {
  if (!userId) return;
  (supabase as any)
    .from("user_dashboard_preferences")
    .upsert(
      {
        user_id: userId,
        pinned_widgets: pinned,
        active_template: template,
        cme_collapsed: cmeCollapsed,
        widget_filters: widgetFilters,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id" },
    )
    .then(({ error }: { error: any }) => {
      if (error) console.error("Failed to persist dashboard preferences", error);
    });
}

export function DashboardWidgetsProvider({ children }: { children: ReactNode }) {
  // Read auth from the shared context — no per-component getUser call.
  const { session, loading: authLoading } = useUserProfile();
  const userId = session?.user.id ?? null;

  // Tier gate: widgets are filtered to the user's subscription tier so a
  // Select user's /dashboard never offers widgets whose source tools sit
  // behind Choice-gated routes. Stored pinned list stays unfiltered so a
  // re-upgrade restores previously-pinned widgets without the user having
  // to re-pin them.
  const { tier: userTier } = useSubscriptionTier();

  const [pinnedWidgets, setPinnedWidgets] = useState<string[]>(loadPinnedLocal);
  const [activeTemplate, setActiveTemplate] = useState<string | null>(loadLocal(TEMPLATE_KEY));
  const [cmeCollapsed, setCmeCollapsedState] = useState(() => loadLocal(CME_KEY) === "true");
  const [widgetFilters, setWidgetFiltersState] = useState<WidgetFilters>(loadFiltersLocal);
  const userIdRef = useRef<string | null>(null);
  userIdRef.current = userId;
  // Track whether user has made any changes before DB load completes
  const userModifiedRef = useRef(false);
  const dbLoadedRef = useRef(false);

  // Refs that always hold current values — used by persist() to avoid stale closures
  const pinnedRef = useRef(pinnedWidgets);
  const templateRef = useRef(activeTemplate);
  const cmeRef = useRef(cmeCollapsed);
  const filtersRef = useRef(widgetFilters);
  pinnedRef.current = pinnedWidgets;
  templateRef.current = activeTemplate;
  cmeRef.current = cmeCollapsed;
  filtersRef.current = widgetFilters;

  // Load preferences from DB once auth resolves and we have a user.
  // No supabase.auth.getUser() call — userId comes from the shared
  // UserProfileProvider context.
  useEffect(() => {
    if (authLoading || !userId) return;
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any)
        .from("user_dashboard_preferences")
        .select("pinned_widgets, active_template, cme_collapsed, widget_filters")
        .eq("user_id", userId)
        .maybeSingle();

      if (cancelled) return;
      dbLoadedRef.current = true;

      if (data && !userModifiedRef.current) {
        const dbPinned = Array.isArray(data.pinned_widgets) ? mergeNewDefaults(data.pinned_widgets as string[]) : getDefaults();
        const dbFilters = data.widget_filters && typeof data.widget_filters === "object" && !Array.isArray(data.widget_filters)
          ? data.widget_filters as WidgetFilters : {};
        setPinnedWidgets(dbPinned);
        setActiveTemplate(data.active_template ?? null);
        setCmeCollapsedState(data.cme_collapsed ?? false);
        setWidgetFiltersState(dbFilters);
        saveLocal(STORAGE_KEY, JSON.stringify(dbPinned));
        saveLocal(TEMPLATE_KEY, data.active_template ?? "");
        saveLocal(CME_KEY, String(data.cme_collapsed ?? false));
        saveLocal(FILTERS_KEY, JSON.stringify(dbFilters));
      } else if (!data) {
        persistToDb(userId, pinnedRef.current, templateRef.current, cmeRef.current, filtersRef.current);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, userId]);

  // Persist current state to both localStorage and DB
  const persist = useCallback((pinned: string[], template: string | null, cme: boolean, filters?: WidgetFilters) => {
    userModifiedRef.current = true;
    const f = filters ?? filtersRef.current;
    saveLocal(STORAGE_KEY, JSON.stringify(pinned));
    saveLocal(TEMPLATE_KEY, template ?? "");
    saveLocal(CME_KEY, String(cme));
    saveLocal(FILTERS_KEY, JSON.stringify(f));
    persistToDb(userIdRef.current, pinned, template, cme, f);
  }, []);

  const addWidget = useCallback((id: string) => {
    setPinnedWidgets(prev => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      persist(next, null, cmeRef.current);
      return next;
    });
    setActiveTemplate(null);
  }, [persist]);

  const removeWidget = useCallback((id: string) => {
    setPinnedWidgets(prev => {
      const next = prev.filter(w => w !== id);
      persist(next, null, cmeRef.current);
      return next;
    });
    setActiveTemplate(null);
  }, [persist]);

  // Tier-filtered visible list. Downstream consumers (Dashboard grid,
  // picker, `isPinned`) read from this so Choice-only widgets stay out
  // of a Select user's view.
  const visiblePinned = useMemo(
    () => filterWidgetIdsByTier(pinnedWidgets, userTier),
    [pinnedWidgets, userTier],
  );

  const isPinned = useCallback((id: string) => visiblePinned.includes(id), [visiblePinned]);

  const toggleWidget = useCallback((id: string) => {
    setPinnedWidgets(prev => {
      const next = prev.includes(id) ? prev.filter(w => w !== id) : [...prev, id];
      persist(next, null, cmeRef.current);
      return next;
    });
    setActiveTemplate(null);
  }, [persist]);

  // Reorder callers (Dashboard grid drag-drop) work in VISIBLE-index
  // space since the grid only renders tier-accessible widgets. Translate
  // those visible indices to storage indices before applying the move,
  // otherwise a Select user dragging widget #1 would accidentally move
  // a Choice-hidden widget at storage position #1.
  const reorderWidgets = useCallback((fromVisibleIndex: number, toVisibleIndex: number) => {
    setPinnedWidgets(prev => {
      const visible = filterWidgetIdsByTier(prev, userTier);
      const movingId = visible[fromVisibleIndex];
      const targetId = visible[toVisibleIndex];
      if (!movingId || !targetId || movingId === targetId) return prev;
      const fromStored = prev.indexOf(movingId);
      const toStored   = prev.indexOf(targetId);
      if (fromStored < 0 || toStored < 0) return prev;
      const next = [...prev];
      next.splice(fromStored, 1);
      const adjustedTarget = fromStored < toStored ? toStored - 1 : toStored;
      next.splice(adjustedTarget, 0, movingId);
      persist(next, templateRef.current, cmeRef.current);
      return next;
    });
  }, [persist, userTier]);

  const applyTemplate = useCallback((id: string): DashboardTemplate | null => {
    const template = TEMPLATE_MAP[id];
    if (!template) return null;
    const cme = !template.cmeExpanded;
    setPinnedWidgets(template.pinnedWidgets);
    setActiveTemplate(id);
    setCmeCollapsedState(cme);
    setWidgetFiltersState({});
    persist(template.pinnedWidgets, id, cme, {});
    return template;
  }, [persist]);

  const clearTemplate = useCallback(() => {
    // Just remove the template label — keep current widgets and filters as-is
    setActiveTemplate(null);
    saveLocal(TEMPLATE_KEY, "");
    persistToDb(userIdRef.current, pinnedRef.current, null, cmeRef.current, filtersRef.current);
  }, []);

  const applyUserTemplate = useCallback((pinned: string[], filters: WidgetFilters, templateId: string) => {
    setPinnedWidgets(pinned);
    setActiveTemplate(templateId);
    setWidgetFiltersState(filters);
    persist(pinned, templateId, cmeRef.current, filters);
  }, [persist]);

  const setWidgetFilter = useCallback((widgetId: string, filtersOrUpdater: Record<string, unknown> | ((prev: Record<string, unknown> | undefined) => Record<string, unknown>)) => {
    setWidgetFiltersState(prev => {
      const current = prev[widgetId];
      const resolved = typeof filtersOrUpdater === "function" ? filtersOrUpdater(current) : filtersOrUpdater;
      const next = { ...prev, [widgetId]: resolved };
      saveLocal(FILTERS_KEY, JSON.stringify(next));
      persistToDb(userIdRef.current, pinnedRef.current, templateRef.current, cmeRef.current, next);
      return next;
    });
  }, []);

  const getWidgetFilters = useCallback((widgetId: string): Record<string, unknown> | undefined => {
    return widgetFilters[widgetId];
  }, [widgetFilters]);

  const setCmeCollapsed = useCallback((collapsed: boolean) => {
    setCmeCollapsedState(collapsed);
    saveLocal(CME_KEY, String(collapsed));
    persistToDb(userIdRef.current, pinnedRef.current, templateRef.current, collapsed, filtersRef.current);
  }, []);

  // Export the TIER-FILTERED list as `pinnedWidgets`. Callers never see
  // Choice widgets on a Select user's dashboard. The underlying storage
  // keeps the full list so re-upgrades restore the user's previous
  // pinned state without extra work.
  const contextValue = useMemo(() => ({
    pinnedWidgets: visiblePinned, addWidget, removeWidget, isPinned, toggleWidget,
    reorderWidgets, activeTemplate, applyTemplate, applyUserTemplate, clearTemplate,
    cmeCollapsed, setCmeCollapsed,
    widgetFilters, setWidgetFilter, getWidgetFilters,
  }), [
    visiblePinned, addWidget, removeWidget, isPinned, toggleWidget,
    reorderWidgets, activeTemplate, applyTemplate, applyUserTemplate, clearTemplate,
    cmeCollapsed, setCmeCollapsed,
    widgetFilters, setWidgetFilter, getWidgetFilters,
  ]);

  return React.createElement(DashboardWidgetsContext.Provider, {
    value: contextValue,
    children,
  });
}

export function useDashboardWidgets() {
  const ctx = useContext(DashboardWidgetsContext);
  if (!ctx) throw new Error("useDashboardWidgets must be used within DashboardWidgetsProvider");
  return ctx;
}
